import type { AxiosRequestConfig } from "axios";
import useSWR, { type Key, type SWRConfiguration, type SWRResponse } from "swr";
import useSWRMutation, {
  type SWRMutationConfiguration,
  type SWRMutationResponse,
} from "swr/mutation";
import { deleteFetcher, fetcher, patchFetcher, postFetcher, putFetcher } from "./fetcher";
import { createSwrConfig, swrConfig } from "./swr-config";

/**
 * Hook for fetching data with GET request
 * @param url API path
 * @param config Axios configuration
 * @param swrOptions SWR configuration
 * @returns SWR response
 */
export function useGet<T>(
  url: Key,
  config?: AxiosRequestConfig,
  swrOptions?: SWRConfiguration,
): SWRResponse<T, Error> {
  const customFetcher = config ? (url: string) => fetcher<T>(url, config) : null; // Use null to let SWR use default fetcher

  const swrResult = useSWR<T, Error>(
    url,
    customFetcher,
    swrOptions ? createSwrConfig(swrOptions) : undefined,
  );

  return swrResult;
}

/**
 * Hook for executing POST requests
 * @param url API path
 * @param swrOptions SWR configuration
 * @returns SWR mutation response
 */
export function usePost<T, D = unknown>(
  url: string,
  swrOptions?: SWRMutationConfiguration<T, Error, string, D>,
): SWRMutationResponse<T, Error, string, D> {
  return useSWRMutation<T, Error, string, D>(
    url,
    (_url: string, { arg }: { arg: D }) => postFetcher<T, D>(_url, arg),
    swrOptions,
  );
}

/**
 * Hook for executing PUT requests
 * @param url API path
 * @param swrOptions SWR configuration
 * @returns SWR mutation response
 */
export function usePut<T, D = unknown>(
  url: string,
  swrOptions?: SWRMutationConfiguration<T, Error, string, D>,
): SWRMutationResponse<T, Error, string, D> {
  return useSWRMutation<T, Error, string, D>(
    url,
    (_url: string, { arg }: { arg: D }) => putFetcher<T, D>(_url, arg),
    swrOptions,
  );
}

/**
 * Hook for executing PATCH requests
 * @param url API path
 * @param swrOptions SWR configuration
 * @returns SWR mutation response
 */
export function usePatch<T, D = unknown>(
  url: string,
  swrOptions?: SWRMutationConfiguration<T, Error, string, D>,
): SWRMutationResponse<T, Error, string, D> {
  return useSWRMutation<T, Error, string, D>(
    url,
    (_url: string, { arg }: { arg: D }) => patchFetcher<T, D>(_url, arg),
    swrOptions,
  );
}

/**
 * Hook for executing DELETE requests
 * @param url API path
 * @param swrOptions SWR configuration
 * @returns SWR mutation response
 */
export function useDelete<T>(
  url: string,
  swrOptions?: SWRMutationConfiguration<T, Error, string, undefined>,
): SWRMutationResponse<T, Error, string, undefined> {
  return useSWRMutation<T, Error, string, undefined>(
    url,
    (_url: string) => deleteFetcher<T>(_url),
    swrOptions,
  );
}

/**
 * Pagination response metadata
 */
export interface PaginationMeta {
  total: number;
  skip: number;
  limit: number;
  hasMore: boolean;
}

/**
 * Pagination response data
 */
export interface PaginationResponse<T> {
  data: T[];
  total: number;
  skip: number;
  limit: number;
  hasMore: boolean;
}

/**
 * Hook for paginated data
 * @param baseUrl API base path
 * @param params Query parameters
 * @param config Axios configuration
 * @param swrOptions SWR configuration
 * @returns SWR response and pagination helper methods
 */
export function usePagination<T, P extends Record<string, unknown>>(
  baseUrl: string | null,
  params: P & { skip?: number; limit?: number } = { limit: 10 } as P & { limit: number },
  config?: AxiosRequestConfig,
  swrOptions?: SWRConfiguration,
) {
  const queryParams = new URLSearchParams();
  if (params) {
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined) {
        queryParams.append(key, String(value));
      }
    });
  }

  const url = baseUrl ? `${baseUrl}?${queryParams.toString()}` : null;
  const response = useGet<PaginationResponse<T>>(url, config, swrOptions);

  return {
    ...response,
    data: response.data?.data || [],
    meta: response.data
      ? {
          total: response.data.total,
          skip: response.data.skip,
          limit: response.data.limit,
          hasMore: response.data.hasMore,
        }
      : undefined,
  };
}

/**
 * Set SWR global configuration
 * @param config Configuration to merge
 */
export function setSWRGlobalConfig(config: Partial<SWRConfiguration>): void {
  Object.assign(swrConfig, config);
}
